import { useEffect, useState, useRef, useMemo } from "react";
import styles from "./page.module.sass";
import { Player, ControlBar, ForwardControl, ReplayControl } from "video-react";
import PropTypes from 'prop-types';
import { renderMedia, isVideoFile, isYouTubeUrl } from "../utils/mediaUtils";
import ImageModal from "../components/ImageModal";
import Carousel from "../components/Carousel";
import CarouselList from "../components/CarouselList";

function Page1({ articleContent, sliderContent, albumSliderContent, pageSliderContent, handleAlbumClick }) {
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const [currentAlbumIndex, setCurrentAlbumIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [modalImageAlt, setModalImageAlt] = useState("");
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  // 依 order 排序相簿清單（升冪）
  const sortedAlbums = useMemo(() => {
    const list = Array.isArray(albumSliderContent) ? [...albumSliderContent] : [];
    return list.sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
  }, [albumSliderContent]);

  // 當相簿索引改變時自動載入相簿內容
  const handleAlbumChange = (index) => {
    if (sortedAlbums && sortedAlbums[index]) {
      const albumId = sortedAlbums[index].id;
      // 模擬點擊事件來觸發 handleAlbumClick
      const fakeEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
        currentTarget: {
          dataset: { id: albumId },
          getAttribute: (name) => {
            if (name === 'data-id' || name === 'id') return albumId;
            return null;
          },
        },
      };
      handleAlbumClick(fakeEvent);
      setCurrentAlbumIndex(index);
    }
  };

  // 當相簿清單載入時，自動載入第一個相簿（已按 order 排序）
  useEffect(() => {
    if (sortedAlbums && sortedAlbums.length > 0 && currentAlbumIndex === 0) {
      handleAlbumChange(0);
    }
  }, [sortedAlbums]);

  const handleImageClick = (imageSrc, imageAlt, imageIndex = 0) => {
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setModalImageIndex(imageIndex);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    // 關閉彈窗後，同步外部列表選中項目
    if (Array.isArray(sliderContent) && sliderContent.length > 0) {
      const idx = Math.max(0, Math.min(modalImageIndex, sliderContent.length - 1));
      setSelectedCardIndex(idx);
    }
    setIsImageModalOpen(false);
  };

  const handlePreviousImage = () => {
    if (!sliderContent || sliderContent.length === 0) return;
    const total = sliderContent.length;
    const newIndex = (modalImageIndex - 1 + total) % total;
    setModalImageIndex(newIndex);
    setModalImageSrc(sliderContent[newIndex].image);
    setModalImageAlt(sliderContent[newIndex].title || "");
  };

  const handleNextImage = () => {
    if (!sliderContent || sliderContent.length === 0) return;
    const total = sliderContent.length;
    const newIndex = (modalImageIndex + 1) % total;
    setModalImageIndex(newIndex);
    setModalImageSrc(sliderContent[newIndex].image);
    setModalImageAlt(sliderContent[newIndex].title || "");
  };
  // 切換相簿後，將列表選中重置為第 0 張，並把「活動照片」列表捲回頂部，避免重新載入時自動跑到底部
  useEffect(() => {
    if (Array.isArray(sliderContent) && sliderContent.length > 0) {
      setSelectedCardIndex(0);
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
    }
  }, [sliderContent]);

  useEffect(() => {
    if (!videoRef.current || !videoContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 當影片進入視窗時播放
            if (videoRef.current && videoRef.current.video) {
              videoRef.current.video.play().catch(error => {
                console.log('影片自動播放失敗:', error);
              });
            }
          } else {
            // 當影片離開視窗時暫停
            if (videoRef.current && videoRef.current.video) {
              videoRef.current.video.pause();
            }
          }
        });
      },
      { 
        threshold: 0.3, // 當影片有 30% 進入視窗時觸發
        rootMargin: '0px 0px -10% 0px' // 稍微提前觸發
      }
    );

    observer.observe(videoContainerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <main className="page-content py-24 table:py-20 mobile:py-16">
      <div className="[&>*:not(:last-child)]:mb-20 [&>*:not(:last-child)]:table::mb-16 max-w-4xl mx-auto w-[90%]">
        <section>
          <div className="text-center">
            <h2 className="text-title font-semibold font-zenOldMincho text-5xl mb-4 table:text-4xl mobile:text-3xl">
              {articleContent.title}
            </h2>
            <h3 className="text-subTitle font-normal text-xl">Introduction</h3>
          </div>
          <div
            className="text-start text-[#373737] font-normal text-xl leading-loose my-11 table:my-8 mobile:my-6"
            dangerouslySetInnerHTML={{ __html: articleContent.content }}
          />
          {Array.isArray(pageSliderContent) && pageSliderContent.length > 0 ? (
            <div className="mb-10 mt-8 mobile:mt-6">
              <Carousel
                items={pageSliderContent.map((s) => ({
                  type: s.fileType === "video" ? "video" : (s.fileType === "youtube" ? "youtube" : "image"),
                  src: s.fileURL,
                  alt: s.title || "",
                }))}
                autoplay={false}
                aspectClassName="aspect-[1920/1080]"
              />
            </div>
          ) : null}
          {articleContent.image && articleContent.image !== "" ? (
            <img
              src={articleContent.image}
              alt=""
              className="m-auto max-h-[372px] object-contain border-8 border-white shadow-xl relative"
            />
          ) : null}
          {articleContent.video && articleContent.video !== "" ? (
            <div 
              ref={videoContainerRef} 
              className="relative group md-l:mx-10"
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Player ref={videoRef} muted poster="/assets/poster.png" src={articleContent.video}>
                <ControlBar autoHide={true}>
                  <ReplayControl seconds={5} order={2.1} />
                  <ForwardControl seconds={5} order={3.1} />
                </ControlBar>
              </Player>
            </div>
          ) : null}
          <div className="trigger" ref={triggerRef}></div>
        </section>

        {albumSliderContent.length > 1 ? (
          <section className="page-album-list">
            <div className="text-center">
              <h2 className="text-title font-semibold font-zenOldMincho text-5xl mb-4 table:text-4xl mobile:text-3xl">
                相簿清單
              </h2>
            </div>
            <div className="px-6">
              <CarouselList
                items={sortedAlbums || []}
                currentIndex={currentAlbumIndex}
                onSelect={(idx) => handleAlbumChange(idx)}
                aspectClassName="aspect-[345/285]"
                followSelection={false}
              />
            </div>
          </section>
        ) : null}

        {sliderContent.length > 0 ? (
          <section className="mb-0">
            <div className="text-center">
              <h2 className="text-title font-semibold font-zenOldMincho text-5xl mb-4 table:text-4xl mobile:text-3xl">
                活動照片
              </h2>
              <h3 className="text-subTitle font-normal text-xl">Recorded photo</h3>
            </div>
            <div className="mt-11 table:mt-8 mobile:mt-6">
              <div>
                <div 
                  className="relative w-full aspect-[1920/1080] bg-black cursor-pointer"
                  onClick={() => {
                    if (sliderContent[selectedCardIndex]?.image && !isVideoFile(sliderContent[selectedCardIndex].image) && !isYouTubeUrl(sliderContent[selectedCardIndex].image)) {
                      handleImageClick(sliderContent[selectedCardIndex].image, sliderContent[selectedCardIndex].title || "", selectedCardIndex);
                    }
                  }}
                >
                  {renderMedia(
                    sliderContent[selectedCardIndex]?.image,
                    "",
                    "absolute inset-0 w-full h-full object-contain",
                    {},
                    false,
                    true,
                    null,
                    false
                  )}
                </div>
              </div>
              <div
                className="h-[450px] m-auto overflow-y-scroll scrollbar"
                ref={listRef}
                style={{ backgroundColor: "#e1c78b" }}
              >
                {sliderContent.map((item, index) => (
                  <div
                    className="flex mobile:flex-col h-[150px] mobile:h-auto justify-start gap-3 mobile:gap-2 p-4 mobile:p-3 border-b border-[#DBDBDB] hover:bg-[#F6F6F6]"
                    key={index}
                    onClick={() => setSelectedCardIndex(index)}
                  >
                    {renderMedia(
                      item.image,
                      "",
                      "aspect-[180/120] object-cover mobile:max-w-32",
                      {},
                      false,
                      false,
                      null,
                      false
                    )}
                    <div>
                      <h3 className="text-[#A93230] font-normal text-xl mb-2 mobile:mb-0 mobile:text-sm">
                        {item.title}
                      </h3>
                      <p className="text-[#696969] font-normal text-l">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null}
      </div>
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
        onClose={closeImageModal}
        images={sliderContent}
        currentIndex={modalImageIndex}
        onPrevious={handlePreviousImage}
        onNext={handleNextImage}
      />
    </main>
  );
}

Page1.propTypes = {
  articleContent: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    image: PropTypes.string,
    video: PropTypes.string
  }).isRequired,
  sliderContent: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string
    })
  ).isRequired,
  albumSliderContent: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      image: PropTypes.string
    })
  ).isRequired,
  pageSliderContent: PropTypes.arrayOf(
    PropTypes.shape({
      fileURL: PropTypes.string,
      fileType: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  handleAlbumClick: PropTypes.func.isRequired
};

export default Page1;
